import { useTranslation } from "react-i18next";
import { Bot, Loader2, Eye, EyeOff, Pencil, Star, ChevronDown } from "lucide-react";
import {
  Card,
  CardContent,
  Button,
  Progress,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@tabipla/ui";
import { usePackingProgress } from "./PackingContext";
import type { PriorityFilter } from "./types";

export function PackingProgress() {
  const { t } = useTranslation();
  const {
    progress,
    priorityCounts,
    isAiLoading,
    showUncheckedOnly,
    priorityFilter,
    isEditMode,
    onAiSuggest,
    onToggleShowUncheckedOnly,
    onPriorityFilterChange,
    onToggleEditMode,
  } = usePackingProgress();

  if (!progress) {
    return null;
  }

  const priorityOptions: { value: PriorityFilter; label: string; count: number }[] = [
    { value: "all", label: t("packing.priorityAll", "すべて"), count: priorityCounts.all },
    { value: "3", label: t("packing.priorityHigh", "必須"), count: priorityCounts.high },
    { value: "2", label: t("packing.priorityMedium", "あると便利"), count: priorityCounts.medium },
    { value: "1", label: t("packing.priorityLow", "余裕があれば"), count: priorityCounts.low },
    { value: "none", label: t("packing.priorityNone", "未設定"), count: priorityCounts.none },
  ];

  const currentOption =
    priorityOptions.find((opt) => opt.value === priorityFilter) ?? priorityOptions[0];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">{t("packing.progress")}</span>
          <span className="text-sm text-muted-foreground">
            {progress.checked} / {progress.total} ({progress.percentage}%)
          </span>
        </div>
        <Progress value={progress.percentage} className="mb-3 h-2" />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAiSuggest}
            disabled={isAiLoading}
            className="h-7 gap-1 text-xs"
          >
            {isAiLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Bot className="h-3 w-3" />
            )}
            <span>{t("packing.aiSuggest")}</span>
          </Button>
          <Button
            variant={showUncheckedOnly ? "default" : "outline"}
            size="sm"
            onClick={onToggleShowUncheckedOnly}
            className="h-7 gap-1 text-xs"
          >
            {showUncheckedOnly ? (
              <>
                <EyeOff className="h-3 w-3" />
                <span>{t("packing.showUncheckedOnly")}</span>
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" />
                <span>{t("packing.showAll")}</span>
              </>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={priorityFilter !== "all" ? "default" : "outline"}
                size="sm"
                className="h-7 gap-1 text-xs"
              >
                <Star className="h-3 w-3" />
                <span>{currentOption.label}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {priorityOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onPriorityFilterChange(option.value)}
                  className={priorityFilter === option.value ? "bg-accent" : ""}
                >
                  <span className="flex-1">{option.label}</span>
                  <span className="ml-2 text-xs text-muted-foreground">({option.count})</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant={isEditMode ? "default" : "outline"}
            size="sm"
            onClick={onToggleEditMode}
            className="h-7 gap-1 text-xs"
          >
            <Pencil className="h-3 w-3" />
            <span>{t("packing.edit")}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
